import { Injectable, NgZone } from '@angular/core';
import { Geolocation, Position } from '@capacitor/geolocation';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, interval, Subscription } from 'rxjs';
import { ConfigurationService } from '../configuration/configuration.service';
import { App } from '@capacitor/app';
import { UtilsService } from '../util/util-service';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { BackgroundGeolocationPlugin } from "@capacitor-community/background-geolocation";
import { NotificationPushService } from '../notification/notification.push.service';

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>("BackgroundGeolocation");

@Injectable({
  providedIn: 'root'
})
export class GeolocationService {
  private intervalSubscription?: Subscription;
  private latestLocation: Position | null = null;
  private watchPositionCallbackId : string = "";

  private currentLocationSubject = new BehaviorSubject<Position | null>(null);
  public currentLocationObservable$ = this.currentLocationSubject.asObservable();
  
  constructor(private _configurationService: ConfigurationService, 
    private _utilsService: UtilsService,
    private _notificationPushService: NotificationPushService,
    private _ngZone: NgZone
  ) {
    this.init();
  }

  private init() {
     App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        // Re-entering foreground: run inside Angular zone
        this._ngZone.run(() => {
          this.startInterval();
        });
      } else {
        // Background: stop to save battery
        this.intervalSubscription?.unsubscribe();
        this.intervalSubscription = undefined;
      }
    });
  }

  async startInterval(): Promise<void> {
    let intervalInSeconds = await this._configurationService.getGeolocationIntervalInSeconds();
    this.intervalSubscription = interval(intervalInSeconds).subscribe(async () => {
       let shouldRetrieveFromAPI = await this._configurationService.shouldRetrieveFromAPI();
       if(shouldRetrieveFromAPI){
         await this._configurationService.getAndSetLocalConfigurations(false);
       }
     });
  }

  getLatestLocation(): Position | null {
    return this.latestLocation;
  }

  async startLocationWatch(): Promise<string> {
    try {
      await this.endLocationWatch();
      let deviceType = this.getDeviceType();
      if (!this.latestLocation) {
        this.latestLocation = await Geolocation.getCurrentPosition({ enableHighAccuracy: false });
      }
      if (this._utilsService.isMobileAndTablet() && deviceType != 'web') {
        await this.startMobileAppLocationWatch();
      } else {
        await this.startWebLocationWatch();
      }

      return this.watchPositionCallbackId;
    } catch (error) {
      return "";
    }

  }

  private async startMobileAppLocationWatch(): Promise<string> {
    const options = {
      backgroundMessage: environment.mobileGeolocationWatchBackgroundMessage,
      backgroundTitle: environment.mobileGeolocationWatchBackgroundTitle,
      requestPermissions: environment.mobileGeolocationWatchRequestPermissions,
      stale: environment.mobileGeolocationWatchStale,
      distanceFilter: environment.mobileGeolocationWatchDistanceFilter
    };
    const id = await BackgroundGeolocation.addWatcher(options, async (location, error) => {
      if (error) {
        return;
      }
      if (location &&
          !this._utilsService.isUndefinedOrNull(location?.latitude) &&
          !this._utilsService.isUndefinedOrNull(location?.longitude)) {
        this.latestLocation = {
          coords: {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy ?? 0,
            altitudeAccuracy: location.altitudeAccuracy ?? 0,
            altitude: location.altitude ?? 0,
            heading: location.bearing ?? 0,
            speed: location.speed ?? 0,
            magneticHeading: null,
            trueHeading: null,
            headingAccuracy: null,
            course: null
          },
          timestamp: location.time ?? 0
        };
        this.currentLocationSubject.next(this.latestLocation);
        if (this._notificationPushService.isOutOfRangeCheck()) {
          await this._notificationPushService.locationRangeCheck(this.latestLocation);
        }
      }
    });
    return this.watchPositionCallbackId = id;
  }
  
  
  private async startWebLocationWatch(): Promise<string> {
    const options = { 
      enableHighAccuracy: environment.webGeolocationWatchEnableHighAccuracy, 
      maximumAge: environment.webGeolocationWatchMaximumAge, 
      timeout: environment.webGeolocationWatchTimeout
    };
    const id = await Geolocation.watchPosition(options, async (devicePosition) => {
      if (devicePosition &&
        !this._utilsService.isUndefinedOrNull(devicePosition?.coords?.latitude) &&
        !this._utilsService.isUndefinedOrNull(devicePosition?.coords?.longitude)) {
        this.latestLocation = devicePosition;
        this.currentLocationSubject.next(devicePosition);
        if (this._notificationPushService.isOutOfRangeCheck()) {
          await this._notificationPushService.locationRangeCheck(this.latestLocation);
        }
      }
    });
    return this.watchPositionCallbackId = id;
  }

  endLocationWatch(): Promise<void> | undefined {
    if (!this._utilsService.isUndefinedOrNullOrEmpty(this.watchPositionCallbackId)) {
      let deviceType = this.getDeviceType();
    
      if (this._utilsService.isMobileAndTablet() && deviceType != 'web') {
        return BackgroundGeolocation.removeWatcher({ id: this.watchPositionCallbackId })
          .then(() => {
            this.watchPositionCallbackId = '';
          });
      } else {
        return Geolocation.clearWatch({ id: this.watchPositionCallbackId })
          .then(() => {
            this.watchPositionCallbackId = '';
          });
      }
    }  
    return undefined;
  }

  private getDeviceType(): string {
    let deviceType = "web";
    switch(Capacitor.getPlatform()) {
      case "ios":
        deviceType = "apns";
        break;
      case "android":
        deviceType = "fcm";
          break;
      default:
        deviceType = "web";
    };
    return deviceType;
  }

}
